import React, { useMemo, useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { PackageIcon, SearchIcon, FilterIcon, XIcon, PlusIcon, EditIcon, TrashIcon, EyeIcon, LayersIcon, AlertCircleIcon, CheckCircleIcon, SaveIcon } from 'lucide-react';
import { MOCK_SUPERSETS, MOCK_IMAGE_SETS, MOCK_IMAGES } from '../schemas/mockData';
import type { Superset, Domain, Level, AgeBand, EmotionalCategory } from '../schemas/types';
import { SupersetModal } from '../components/SupersetModal';
import { SearchInput } from '../components/SearchInput';
/**
 * Supersets Page
 * Manage superset collections with advanced no-repeat policies
 * Includes promotion/prevention filtering for emotional categorization
 */
export function Supersets() {
  const {
    t
  } = useOrganization();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'published' | 'draft'>('all');
  const [selectedAgeBand, setSelectedAgeBand] = useState<AgeBand | 'all'>('all');
  const [selectedEmotionalCategory, setSelectedEmotionalCategory] = useState<EmotionalCategory | 'all'>('all');
  const [showFilters, setShowFilters] = useState(true);
  const [selectedSuperset, setSelectedSuperset] = useState<Superset | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [supersets, setSupersets] = useState<Superset[]>(MOCK_SUPERSETS);
  const [editingSuperset, setEditingSuperset] = useState<Superset | null>(null);
  // Filter supersets
  const filteredSupersets = useMemo(() => {
    return MOCK_SUPERSETS.filter(superset => {
      const matchesSearch = searchQuery === '' || superset.name.toLowerCase().includes(searchQuery.toLowerCase()) || superset.description?.toLowerCase().includes(searchQuery.toLowerCase()) || superset.id.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = selectedStatus === 'all' || superset.status === selectedStatus;
      const matchesAgeBand = selectedAgeBand === 'all' || superset.ageBands.includes(selectedAgeBand);
      // Filter by emotional category - check if superset contains images with the selected category
      let matchesEmotionalCategory = true;
      if (selectedEmotionalCategory !== 'all') {
        const supersetSets = MOCK_IMAGE_SETS.filter(set => superset.setIds.includes(set.id));
        const supersetImages = MOCK_IMAGES.filter(img => supersetSets.some(set => set.images.includes(img.id)));
        matchesEmotionalCategory = supersetImages.some(img => img.emotionalCategory === selectedEmotionalCategory);
      }
      return matchesSearch && matchesStatus && matchesAgeBand && matchesEmotionalCategory;
    });
  }, [searchQuery, selectedStatus, selectedAgeBand, selectedEmotionalCategory]);
  // Calculate statistics
  const stats = useMemo(() => {
    const allSupersetImages = filteredSupersets.flatMap(superset => {
      const sets = MOCK_IMAGE_SETS.filter(set => superset.setIds.includes(set.id));
      return MOCK_IMAGES.filter(img => sets.some(set => set.images.includes(img.id)));
    });
    return {
      totalSupersets: MOCK_SUPERSETS.length,
      filteredSupersets: filteredSupersets.length,
      published: filteredSupersets.filter(s => s.status === 'published').length,
      draft: filteredSupersets.filter(s => s.status === 'draft').length,
      totalSets: filteredSupersets.reduce((sum, s) => sum + s.setIds.length, 0),
      totalImages: allSupersetImages.length,
      promotion: allSupersetImages.filter(img => img.emotionalCategory === 'promotion').length,
      prevention: allSupersetImages.filter(img => img.emotionalCategory === 'prevention').length
    };
  }, [filteredSupersets]);
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('all');
    setSelectedAgeBand('all');
    setSelectedEmotionalCategory('all');
  };
  const hasActiveFilters = searchQuery !== '' || selectedStatus !== 'all' || selectedAgeBand !== 'all' || selectedEmotionalCategory !== 'all';
  // Get sets for a specific superset
  const getSupersetSets = (superset: Superset) => {
    return MOCK_IMAGE_SETS.filter(set => superset.setIds.includes(set.id));
  };
  // Get all images in a superset
  const getSupersetImages = (superset: Superset) => {
    const sets = getSupersetSets(superset);
    return MOCK_IMAGES.filter(img => sets.some(set => set.images.includes(img.id)));
  };
  // Calculate emotional category distribution for a superset
  const getSupersetEmotionalDistribution = (superset: Superset) => {
    const images = getSupersetImages(superset);
    const promotion = images.filter(img => img.emotionalCategory === 'promotion').length;
    const prevention = images.filter(img => img.emotionalCategory === 'prevention').length;
    return {
      promotion,
      prevention,
      total: images.length
    };
  };
  // Calculate grid coverage for a superset
  const getGridCoverage = (superset: Superset) => {
    const domains: Domain[] = ['Self', 'Material', 'Social', 'Spiritual'];
    const levels: Level[] = ['Foundational', 'Experiential', 'Aspirational'];
    let filled = 0;
    let total = domains.length * levels.length;
    const sets = getSupersetSets(superset);
    domains.forEach(domain => {
      levels.forEach(level => {
        const hasImages = sets.some(set => set.grid[domain]?.[level]?.length > 0);
        if (hasImages) filled++;
      });
    });
    return {
      filled,
      total,
      percentage: Math.round(filled / total * 100)
    };
  };
  // Calculate capacity per cell
  const getCellCapacity = (superset: Superset) => {
    const domains: Domain[] = ['Self', 'Material', 'Social', 'Spiritual'];
    const levels: Level[] = ['Foundational', 'Experiential', 'Aspirational'];
    const capacity: {
      [key: string]: number;
    } = {};
    const sets = getSupersetSets(superset);
    domains.forEach(domain => {
      levels.forEach(level => {
        const key = `${domain}-${level}`;
        const images = sets.flatMap(set => set.grid[domain]?.[level] || []);
        capacity[key] = images.length;
      });
    });
    return capacity;
  };
  // CRUD Handlers
  const handleCreateSuperset = (newSuperset: Omit<Superset, 'id' | 'createdAt' | 'updatedAt' | 'version'>) => {
    const superset: Superset = {
      ...newSuperset,
      id: `superset-${Date.now()}`,
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setSupersets([...supersets, superset]);
    setShowCreateModal(false);
  };
  const handleUpdateSuperset = (updatedSuperset: Superset) => {
    setSupersets(supersets.map(s => s.id === updatedSuperset.id ? {
      ...updatedSuperset,
      updatedAt: new Date().toISOString()
    } : s));
    setShowCreateModal(false);
    setEditingSuperset(null);
  };
  const openEditModal = (superset: Superset) => {
    setEditingSuperset(superset);
    setShowCreateModal(true);
  };
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-2630">
      {/* Header */}
      <div className="mb-8" data-id="element-2631">
        <div className="flex items-center justify-between mb-3" data-id="element-2632">
          <div className="flex items-center gap-3" data-id="element-2633">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-2634">
              <PackageIcon className="w-7 h-7 text-white" data-id="element-2635" />
            </div>
            <div data-id="element-2636">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-2637">Supersets</h1>
              <p className="text-gray-600 mt-1" data-id="element-2638">
                Manage superset collections with advanced policies
              </p>
            </div>
          </div>
          <button onClick={() => setShowCreateModal(true)} className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all font-semibold shadow-lg flex items-center gap-2" data-id="element-2639">
            <PlusIcon className="w-5 h-5" data-id="element-2640" />
            Create Superset
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-8 gap-4 mb-6" data-id="element-2641">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200" data-id="element-2642">
          <div className="text-sm font-semibold text-blue-900 mb-1" data-id="element-2643">Total</div>
          <div className="text-2xl font-bold text-blue-700" data-id="element-2644">
            {stats.totalSupersets}
          </div>
        </div>
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border-2 border-indigo-200" data-id="element-2645">
          <div className="text-sm font-semibold text-indigo-900 mb-1" data-id="element-2646">
            Filtered
          </div>
          <div className="text-2xl font-bold text-indigo-700" data-id="element-2647">
            {stats.filteredSupersets}
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200" data-id="element-2648">
          <div className="text-sm font-semibold text-green-900 mb-1" data-id="element-2649">
            Published
          </div>
          <div className="text-2xl font-bold text-green-700" data-id="element-2650">
            {stats.published}
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border-2 border-orange-200" data-id="element-2651">
          <div className="text-sm font-semibold text-orange-900 mb-1" data-id="element-2652">
            Draft
          </div>
          <div className="text-2xl font-bold text-orange-700" data-id="element-2653">
            {stats.draft}
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200" data-id="element-2654">
          <div className="text-sm font-semibold text-purple-900 mb-1" data-id="element-2655">Sets</div>
          <div className="text-2xl font-bold text-purple-700" data-id="element-2656">
            {stats.totalSets}
          </div>
        </div>
        <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl p-4 border-2 border-pink-200" data-id="element-2657">
          <div className="text-sm font-semibold text-pink-900 mb-1" data-id="element-2658">Images</div>
          <div className="text-2xl font-bold text-pink-700" data-id="element-2659">
            {stats.totalImages}
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border-2 border-emerald-200" data-id="element-2660">
          <div className="text-sm font-semibold text-emerald-900 mb-1" data-id="element-2661">
            Promotion
          </div>
          <div className="text-2xl font-bold text-emerald-700" data-id="element-2662">
            {stats.promotion}
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border-2 border-amber-200" data-id="element-2663">
          <div className="text-sm font-semibold text-amber-900 mb-1" data-id="element-2664">
            Prevention
          </div>
          <div className="text-2xl font-bold text-amber-700" data-id="element-2665">
            {stats.prevention}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6 mb-6" data-id="element-2666">
        <div className="flex items-center justify-between mb-4" data-id="element-2667">
          <div className="flex items-center gap-2" data-id="element-2668">
            <FilterIcon className="w-5 h-5 text-gray-600" data-id="element-2669" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-2670">Filters</h2>
            {hasActiveFilters && <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full" data-id="element-2671">
                Active
              </span>}
          </div>
          <div className="flex items-center gap-2" data-id="element-2672">
            {hasActiveFilters && <button onClick={clearFilters} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1" data-id="element-2673">
                <XIcon className="w-4 h-4" data-id="element-2674" />
                Clear
              </button>}
            <button onClick={() => setShowFilters(!showFilters)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-2675">
              {showFilters ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        {showFilters && <div className="space-y-4" data-id="element-2676">
            {/* Search - NOW USING SHARED COMPONENT */}
            <div data-id="element-2677">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2678">
                Search
              </label>
              <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Search by name, description, or ID..." data-id="element-2679" />
            </div>

            {/* Filter Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2680">
              {/* Emotional Category Filter */}
              <div data-id="element-2681">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2682">
                  Emotional Category
                </label>
                <select value={selectedEmotionalCategory} onChange={e => setSelectedEmotionalCategory(e.target.value as EmotionalCategory | 'all')} className="w-full py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" data-id="element-2683">
                  <option value="all" data-id="element-2684">All Categories</option>
                  <option value="promotion" data-id="element-2685">Promotion</option>
                  <option value="prevention" data-id="element-2686">Prevention</option>
                </select>
              </div>
              {/* Status Filter */}
              <div data-id="element-2687">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2688">
                  Status
                </label>
                <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value as 'all' | 'published' | 'draft')} className="w-full py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" data-id="element-2689">
                  <option value="all" data-id="element-2690">All Status</option>
                  <option value="published" data-id="element-2691">Published</option>
                  <option value="draft" data-id="element-2692">Draft</option>
                </select>
              </div>
              {/* Age Band Filter */}
              <div data-id="element-2693">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2694">
                  Age Band
                </label>
                <select value={selectedAgeBand} onChange={e => setSelectedAgeBand(e.target.value as AgeBand | 'all')} className="w-full py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" data-id="element-2695">
                  <option value="all" data-id="element-2696">All Age Bands</option>
                  <option value="5-9" data-id="element-2697">5-9 years</option>
                  <option value="10-14" data-id="element-2698">10-14 years</option>
                  <option value="14-18" data-id="element-2699">14-18 years</option>
                  <option value="Adults" data-id="element-2700">Adults</option>
                  <option value="Students" data-id="element-2701">Students</option>
                </select>
              </div>
            </div>
          </div>}
      </div>

      {/* Supersets Grid */}
      <div className="grid grid-cols-1 gap-6" data-id="element-2702">
        {filteredSupersets.map(superset => {
        const coverage = getGridCoverage(superset);
        const emotionalDist = getSupersetEmotionalDistribution(superset);
        const sets = getSupersetSets(superset);
        return <div key={superset.id} className="bg-white rounded-xl shadow-sm border-2 border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all overflow-hidden" data-id="element-2703">
              {/* Header */}
              <div className="p-6 border-b border-gray-200" data-id="element-2704">
                <div className="flex items-start justify-between mb-4" data-id="element-2705">
                  <div className="flex-1" data-id="element-2706">
                    <div className="flex items-center gap-2 mb-2" data-id="element-2707">
                      <h3 className="text-xl font-bold text-gray-900" data-id="element-2708">
                        {superset.name}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-bold rounded-full ${superset.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-2709">
                        {superset.status}
                      </span>
                    </div>
                    {superset.description && <p className="text-sm text-gray-600 mb-2" data-id="element-2710">
                        {superset.description}
                      </p>}
                    <div className="text-xs text-gray-500 font-mono" data-id="element-2711">
                      {superset.id}
                    </div>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-2712">
                    <button onClick={() => setSelectedSuperset(superset)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="View details" data-id="element-2713">
                      <EyeIcon className="w-4 h-4" data-id="element-2714" />
                    </button>
                    <button onClick={() => openEditModal(superset)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit" data-id="element-2715">
                      <EditIcon className="w-4 h-4" data-id="element-2716" />
                    </button>
                    <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" data-id="element-2717">
                      <TrashIcon className="w-4 h-4" data-id="element-2718" />
                    </button>
                  </div>
                </div>

                {/* Emotional Category Distribution */}
                <div className="flex items-center gap-3 mb-4" data-id="element-2719">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-lg" data-id="element-2720">
                    <div className="w-2 h-2 bg-green-500 rounded-full" data-id="element-2721"></div>
                    <span className="text-sm font-semibold text-green-700" data-id="element-2722">
                      Promotion: {emotionalDist.promotion}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 rounded-lg" data-id="element-2723">
                    <div className="w-2 h-2 bg-orange-500 rounded-full" data-id="element-2724"></div>
                    <span className="text-sm font-semibold text-orange-700" data-id="element-2725">
                      Prevention: {emotionalDist.prevention}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-lg" data-id="element-2726">
                    <LayersIcon className="w-4 h-4 text-blue-600" data-id="element-2727" />
                    <span className="text-sm font-semibold text-blue-700" data-id="element-2728">
                      {sets.length} sets
                    </span>
                  </div>
                </div>

                {/* Grid Coverage */}
                <div className="mb-4" data-id="element-2729">
                  <div className="flex items-center justify-between mb-2" data-id="element-2730">
                    <span className="text-sm font-semibold text-gray-700" data-id="element-2731">
                      Grid Coverage
                    </span>
                    <span className="text-sm font-bold text-indigo-600" data-id="element-2732">
                      {coverage.percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2733">
                    <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 h-2 rounded-full transition-all" style={{
                  width: `${coverage.percentage}%`
                }} data-id="element-2734" />
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-2735">
                    {coverage.filled} of {coverage.total} cells filled
                  </div>
                </div>

                {/* Metadata */}
                <div className="flex items-center gap-4 text-xs text-gray-600" data-id="element-2736">
                  <div data-id="element-2737">v{superset.version}</div>
                  <div data-id="element-2738">{superset.ageBands.join(', ')}</div>
                  <div data-id="element-2739">{superset.locales.join(', ').toUpperCase()}</div>
                </div>
              </div>

              {/* Policies Section */}
              <div className="p-6 bg-gray-50" data-id="element-2740">
                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-2741">
                  <AlertCircleIcon className="w-4 h-4 text-indigo-600" data-id="element-2742" />
                  Superset Policies
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-2743">
                  <div className="bg-white rounded-lg p-3 border border-gray-200" data-id="element-2744">
                    <div className="text-xs text-gray-600 mb-1" data-id="element-2745">
                      No-Repeat Policy
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2746">
                      {superset.policies.avoidRepeatsPerParticipant ? <>
                          <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-2747" />
                          <span className="text-sm font-semibold text-gray-900" data-id="element-2748">
                            {superset.policies.repeatWindowSessions} sessions
                          </span>
                        </> : <>
                          <XIcon className="w-4 h-4 text-gray-400" data-id="element-2749" />
                          <span className="text-sm text-gray-500" data-id="element-2750">
                            Disabled
                          </span>
                        </>}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200" data-id="element-2751">
                    <div className="text-xs text-gray-600 mb-1" data-id="element-2752">
                      Fill Strategy
                    </div>
                    <div className="text-sm font-semibold text-gray-900 capitalize" data-id="element-2753">
                      {superset.policies.fillStrategy}
                    </div>
                  </div>
                </div>
              </div>
            </div>;
      })}
      </div>

      {/* Empty State */}
      {filteredSupersets.length === 0 && <div className="text-center py-12" data-id="element-2754">
          <PackageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" data-id="element-2755" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2756">
            No supersets found
          </h3>
          <p className="text-gray-600 mb-4" data-id="element-2757">
            {hasActiveFilters ? 'Try adjusting your filters' : 'Create your first superset to get started'}
          </p>
        </div>}

      {/* Updated Create/Edit Superset Modal */}
      <SupersetModal isOpen={showCreateModal} onClose={() => {
      setShowCreateModal(false);
      setEditingSuperset(null);
    }} onSave={editingSuperset ? handleUpdateSuperset : handleCreateSuperset} editingSuperset={editingSuperset} data-id="element-2758" />

      {/* Superset Details Modal */}
      {selectedSuperset && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in" data-id="element-2759">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-scale-in" data-id="element-2760">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-2761">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-2762">
                {selectedSuperset.name}
              </h2>
              <button onClick={() => setSelectedSuperset(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-2763">
                <XIcon className="w-6 h-6 text-gray-600" data-id="element-2764" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6" data-id="element-2765">
              <div className="space-y-6" data-id="element-2766">
                {/* Sets in Superset */}
                <div data-id="element-2767">
                  <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-2768">
                    Image Sets
                  </h3>
                  <div className="grid grid-cols-1 gap-4" data-id="element-2769">
                    {getSupersetSets(selectedSuperset).map(set => <div key={set.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-2770">
                        <div className="flex items-center justify-between mb-2" data-id="element-2771">
                          <h4 className="font-semibold text-gray-900" data-id="element-2772">
                            {set.name}
                          </h4>
                          <span className="text-xs text-gray-500" data-id="element-2773">
                            {set.images.length} images
                          </span>
                        </div>
                        {set.description && <p className="text-sm text-gray-600" data-id="element-2774">
                            {set.description}
                          </p>}
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}